import type { FeedbackEvent, FeedbackKind, FeedbackStatus } from '../lib/api'
import './feedback.css'

/** The pieces the customer's Feedback tab and the operator's console both
 *  draw (migration 065), so a status reads the same on both sides of one
 *  report. Every class is `fb-` prefixed, modifiers included -- see the head
 *  of feedback.css for what an unprefixed one cost. */

export const STATUS_WORDS: Record<FeedbackStatus, string> = {
  new: 'New',
  need_info: 'Need more information',
  accepted: 'Accepted',
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Declined',
}

/** WHAT THE CUSTOMER READS. "Need more information" is how it looks from the
 *  operator's chair; to the person who has to answer, it is waiting on him. */
export const CUSTOMER_WORDS: Record<FeedbackStatus, string> = {
  ...STATUS_WORDS,
  need_info: 'Waiting on you',
}

export const STATUS_ORDER: FeedbackStatus[] = [
  'new', 'need_info', 'accepted', 'in_progress', 'done', 'declined',
]

export function day(iso: string): string {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' })
}

export function stamp(iso: string): string {
  const d = new Date(iso)
  return `${day(iso)} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

export function StatusPill({ status, at, forCustomer }: {
  status: FeedbackStatus
  at?: string | null
  /** The customer's side: his own words for a status. */
  forCustomer?: boolean
}) {
  const words = forCustomer ? CUSTOMER_WORDS : STATUS_WORDS
  return (
    <span className={`fb-pill fb-${status}`}>
      {words[status]}{status === 'done' && at ? ` · ${day(at)}` : ''}
    </span>
  )
}

export function KindChip({ kind }: { kind: FeedbackKind }) {
  return <span className={`fb-kind fb-${kind}`}>{kind === 'bug' ? 'Bug' : 'Feature'}</span>
}

const PATH: FeedbackStatus[] = ['new', 'accepted', 'in_progress', 'done']
const PATH_WORDS = ['Sent', 'Accepted', 'In progress', 'Done']

/** Sent, Accepted, In progress, Done -- with the day each was reached. A
 *  report that was declined, or is waiting on the customer, is not on that
 *  road, and says so in words instead of pretending to be part-way along. */
export function Steps({ status, createdAt, events }: {
  status: FeedbackStatus
  createdAt: string
  events: FeedbackEvent[]
}) {
  if (status === 'declined') {
    return <div className="fb-offroad">Declined. The conversation below says why.</div>
  }
  const reached = (s: FeedbackStatus): string | null => {
    if (s === 'new') return createdAt
    // THE LAST TIME, not the first: a report done, reopened and done again
    // shows the day it was done for good, which is the day on the pill.
    const hits = events.filter((e) => e.kind === 'status' && e.status_to === s)
    return hits.length ? hits[hits.length - 1].created_at : null
  }
  const at = status === 'need_info' ? 0 : PATH.indexOf(status)
  return (
    <>
      <div className="fb-steps">
        {PATH.map((s, i) => {
          const when = reached(s)
          const cls = i < at || (i === at && s === 'done') ? 'fb-reached' : i === at ? 'fb-current' : ''
          return (
            <div className={`fb-step ${cls}`} key={s}>
              {PATH_WORDS[i]}
              <small>{i <= at && when ? day(when) : ''}</small>
            </div>
          )
        })}
      </div>
      {status === 'need_info' && (
        <div className="fb-offroad fb-urgent">
          More information is needed before this can move. Please answer below.
        </div>
      )}
    </>
  )
}

/** The conversation, oldest first. `forOperator` is the console: it shows
 *  private notes, which the customer's side is never even sent. */
export function Thread({ events, forOperator }: {
  events: FeedbackEvent[]
  forOperator?: boolean
}) {
  return (
    <div className="fb-thread">
      {events.map((e) => {
        if (e.kind === 'status') {
          return (
            <div className="fb-moved" key={e.id}>
              {forOperator ? (e.author_label ?? 'Operator') : 'Advance Talk'} moved this to{' '}
              <b>{e.status_to ? STATUS_WORDS[e.status_to] : ''}</b> · {stamp(e.created_at)}
            </div>
          )
        }
        if (e.kind === 'note') {
          return (
            <div className="fb-say fb-private" key={e.id}>
              {e.body}
              <span className="fb-meta">
                Private note · {e.author_label ?? 'Operator'} · {stamp(e.created_at)} · the customer does not see this
              </span>
            </div>
          )
        }
        return (
          <div className={`fb-say ${e.from_operator ? 'fb-us' : 'fb-them'}`} key={e.id}>
            {e.body}
            <span className="fb-meta">{e.author_label ?? ''} · {stamp(e.created_at)}</span>
          </div>
        )
      })}
    </div>
  )
}
